'use client';

import { motion } from 'framer-motion';
import { X } from 'lucide-react';

interface HowToPlayModalProps {
  onClose: () => void;
  strings: {
    title: string;
    closeLabel: string;
    introLine1: string;
    introLine2: string;
    colorHint: string;
    examples: Array<{
      word: string[];
      highlightIndex: number;
      color: 'correct' | 'present' | 'absent';
      label: string;
    }>;
  };
}

export function HowToPlayModal({ onClose, strings }: HowToPlayModalProps) {
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
      />

      <motion.div
        initial={{ scale: 0.9, y: 40, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.95, y: 20, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        className="
          relative
          w-[92%]
          max-w-md
          rounded-xl
          bg-neutral-900
          p-6
          text-white
          shadow-xl
        "
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {strings.title}
          </h2>
          <button
            onClick={onClose}
            aria-label={strings.closeLabel}
            className="rounded-md p-1 hover:bg-neutral-800 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>


        <div className="space-y-3 text-sm text-neutral-300">
          <p>
            {strings.introLine1}
            <br />
            {strings.introLine2}
          </p>

          <p>
            {strings.colorHint}
          </p>
        </div>

        <div className="mt-5 space-y-4">
          {strings.examples.map((example, index) => (
            <ExampleRow
              key={`${example.word.join('')}-${index}`}
              word={example.word}
              highlightIndex={example.highlightIndex}
              color={example.color}
              label={example.label}
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

function ExampleRow({
  word,
  highlightIndex,
  color,
  label,
}: {
  word: string[];
  highlightIndex: number;
  color: 'correct' | 'present' | 'absent';
  label: string;
}) {
  const colorClasses = {
    correct: 'bg-[var(--color-game-letter-correct)]',
    present: 'bg-[var(--color-game-letter-present)]',
    absent: 'bg-[var(--color-game-letter-absent)]',
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-1">
        {word.map((letter, i) => (
          <div
            key={i}
            className={`
              h-10 w-10
              flex items-center justify-center
              rounded-md
              border border-neutral-700
              font-bold
              ${
                i === highlightIndex
                  ? `${colorClasses[color]} text-white`
                  : 'bg-neutral-800 text-neutral-300'
              }
            `}
          >
            {letter}
          </div>
        ))}
      </div>
      <p className="text-xs text-neutral-400">
        {label}
      </p>
    </div>
  );
}
